import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAddPayment, useItemPaymentSummary } from '../../hooks/usePayments';
import { Dialog, Button, Input, Label, Select } from '../ui';
import { formatCurrency } from '../../lib/utils';

const AddPaymentForm = React.memo(function AddPaymentForm({
  isOpen,
  onClose,
  itemId,
  itemData
}) {
  const addPayment = useAddPayment();
  const { data: summary } = useItemPaymentSummary(itemId);

  const [formData, setFormData] = useState({
    amount: '',
    payment_type: 'item_cost',
    payment_method: 'cash',
    payment_date: new Date().toISOString().split('T')[0],
    note: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        amount: '',
        payment_type: 'item_cost',
        payment_method: 'cash',
        payment_date: new Date().toISOString().split('T')[0],
        note: ''
      });
      setErrors({});
    }
  }, [isOpen]);

  const remaining = summary?.remaining || 0;

  const handlePayFull = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      amount: remaining > 0 ? remaining.toFixed(2) : ''
    }));
  }, [remaining]);

  const handlePayItemCost = useCallback(() => {
    const itemRemaining = Math.max(0, 
      (summary?.purchasePriceGel || 0) - (summary?.itemCostPaid || 0)
    );
    setFormData(prev => ({
      ...prev,
      amount: itemRemaining.toFixed(2),
      payment_type: 'item_cost'
    }));
  }, [summary]);

  const handlePayWeight = useCallback(() => {
    const weightRemaining = Math.max(0, 
      (summary?.weightCostGel || 0) - (summary?.weightCostPaid || 0)
    );
    setFormData(prev => ({
      ...prev,
      amount: weightRemaining.toFixed(2),
      payment_type: 'weight_cost'
    }));
  }, [summary]);

  const handlePayCustoms = useCallback(() => {
    const customsRemaining = Math.max(0, 
      (summary?.customsCostGel || 0) - (summary?.customsCostPaid || 0)
    );
    setFormData(prev => ({
      ...prev,
      amount: customsRemaining.toFixed(2),
      payment_type: 'customs_cost'
    }));
  }, [summary]);

  const validate = useCallback(() => {
    const newErrors = {};

    if (!formData.amount) {
      newErrors.amount = 'თანხა აუცილებელია';
    } else if (parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'თანხა უნდა იყოს > 0';
    }

    if (!formData.payment_type) {
      newErrors.payment_type = 'ტიპი აუცილებელია';
    }

    if (!formData.payment_date) {
      newErrors.payment_date = 'თარიღი აუცილებელია';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!validate()) return;

    addPayment.mutate({
      item_id: itemId,
      amount: parseFloat(formData.amount),
      payment_type: formData.payment_type,
      payment_method: formData.payment_method,
      payment_date: formData.payment_date,
      note: formData.note || null
    }, {
      onSuccess: () => {
        onClose();
      }
    });
  }, [formData, validate, addPayment, itemId, onClose]);

  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  if (!isOpen) return null;

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="გადახდის დამატება">
      <form onSubmit={handleSubmit} className="space-y-4" aria-label="გადახდის დამატების ფორმა">
        {summary && remaining > 0 && (
          <div className="bg-muted p-3 rounded-lg">
            <div className="text-sm text-muted-foreground">დარჩენილი თანხა:</div>
            <div className="text-lg font-bold text-red-500">
              {formatCurrency(remaining)}
            </div>
          </div>
        )}

        <div>
          <Label htmlFor="payment-amount">თანხა (₾) *</Label>
          <Input
            id="payment-amount"
            type="number"
            step="0.01"
            min="0.01"
            value={formData.amount}
            onChange={(e) => handleFieldChange('amount', e.target.value)}
            placeholder="0.00"
            aria-required="true"
            aria-invalid={!!errors.amount}
          />
          {errors.amount && (
            <p className="text-red-500 text-xs mt-1">{errors.amount}</p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handlePayFull}
            disabled={remaining <= 0}
          >
            სრულად
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handlePayItemCost}
          >
            ნივთის ფასი
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handlePayWeight}
            disabled={!summary?.weightCostGel}
          >
            წონა
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handlePayCustoms}
            disabled={!summary?.customsCostGel}
          >
            განბაჟება
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="payment-type">ტიპი *</Label>
            <Select
              id="payment-type"
              value={formData.payment_type}
              onChange={(e) => handleFieldChange('payment_type', e.target.value)}
              aria-required="true"
            >
              <option value="item_cost">ნივთის ფასი</option>
              <option value="weight_cost">წონა</option>
              <option value="customs_cost">განბაჟება</option>
              <option value="other">სხვა</option>
            </Select>
          </div>
          <div>
            <Label htmlFor="payment-method">მეთოდი</Label>
            <Select
              id="payment-method"
              value={formData.payment_method}
              onChange={(e) => handleFieldChange('payment_method', e.target.value)}
            >
              <option value="cash">ნაღდი</option>
              <option value="transfer">გადარიცხვა</option>
              <option value="other">სხვა</option>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="payment-date">თარიღი *</Label>
          <Input
            id="payment-date"
            type="date"
            value={formData.payment_date}
            onChange={(e) => handleFieldChange('payment_date', e.target.value)}
            aria-required="true"
            aria-invalid={!!errors.payment_date}
          />
          {errors.payment_date && (
            <p className="text-red-500 text-xs mt-1">{errors.payment_date}</p>
          )}
        </div>

        <div>
          <Label htmlFor="payment-note">შენიშვნა</Label>
          <Input
            id="payment-note"
            value={formData.note}
            onChange={(e) => handleFieldChange('note', e.target.value)}
            placeholder="დამატებითი ინფორმაცია..."
          />
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
          >
            გაუქმება
          </Button>
          <Button
            type="submit"
            disabled={addPayment.isPending}
          >
            {addPayment.isPending ? 'იმატება...' : 'დამატება'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
});

AddPaymentForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  itemId: PropTypes.string.isRequired,
  itemData: PropTypes.object,
};

export default AddPaymentForm;
